import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Sync as SyncIcon,
  Api as ApiIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { setTheme } from '../store/slices/uiSlice';
import PermissionGate from '../components/Common/PermissionGate';
import { usePermission } from '../hooks/usePermissions';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface AppSettings {
  apiBaseUrl: string;
  syncInterval: number;
  maxRetries: number;
  requestTimeout: number;
  enableLogging: boolean;
  logLevel: string;
}

interface NotificationSettings {
  enableOrderNotifications: boolean;
  enableSyncNotifications: boolean;
  enableErrorNotifications: boolean;
  soundEnabled: boolean;
  notificationDuration: number;
}

interface SyncSettings {
  autoSyncEnabled: boolean;
  syncInterval: number;
  syncOnStartup: boolean;
  syncMenuItems: boolean;
  syncOrders: boolean;
  syncBranches: boolean;
}

const SettingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => (state as any).ui?.theme || 'light');
  const notifications = useAppSelector((state) => (state as any).ui?.notifications || true);
  const [tabValue, setTabValue] = useState(0);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Permission checks - using existing permissions since settings specific ones don't exist
  const canViewSettings = true; // Basic settings view is available to all authenticated users
  const canUpdateSettings = usePermission('system:superadmin_manage'); // Only admins can modify settings
  const canManageApiKeys = usePermission('system:superadmin_manage'); // Only admins can manage API keys
  const canManageData = usePermission('system:superadmin_manage'); // Only admins can manage data

  // App Settings
  const [appSettings, setAppSettings] = useState<AppSettings>({
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'https://localhost:5001/api',
    syncInterval: 30,
    maxRetries: 3,
    requestTimeout: 30000,
    enableLogging: true,
    logLevel: 'Info',
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    enableOrderNotifications: true,
    enableSyncNotifications: true,
    enableErrorNotifications: true,
    soundEnabled: true,
    notificationDuration: 5000,
  });

  // Sync Settings
  const [syncSettings, setSyncSettings] = useState<SyncSettings>({
    autoSyncEnabled: true,
    syncInterval: 30,
    syncOnStartup: true,
    syncMenuItems: true,
    syncOrders: true,
    syncBranches: true,
  });

  // API Keys Management
  const [apiKeys, setApiKeys] = useState([
    { id: 1, name: 'Talabat API Key', value: '*********************', created: '2024-01-15' },
    { id: 2, name: 'Webhook Secret', value: '*********************', created: '2024-01-10' },
  ]);
  const [newApiKeyDialog, setNewApiKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState({ name: '', value: '' });

  useEffect(() => {
    // Load settings from localStorage or API
    loadSettings();
  }, []);

  const loadSettings = () => {
    try {
      const savedAppSettings = localStorage.getItem('appSettings');
      if (savedAppSettings) {
        setAppSettings(JSON.parse(savedAppSettings));
      }

      const savedNotificationSettings = localStorage.getItem('notificationSettings');
      if (savedNotificationSettings) {
        setNotificationSettings(JSON.parse(savedNotificationSettings));
      }

      const savedSyncSettings = localStorage.getItem('syncSettings');
      if (savedSyncSettings) {
        setSyncSettings(JSON.parse(savedSyncSettings));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveSettings = async () => {
    try {
      // Save to localStorage (in a real app, you'd save to an API)
      localStorage.setItem('appSettings', JSON.stringify(appSettings));
      localStorage.setItem('notificationSettings', JSON.stringify(notificationSettings));
      localStorage.setItem('syncSettings', JSON.stringify(syncSettings));      // Update Redux store
      // Note: Redux UI slice handles notifications differently
      // You may want to add specific notification actions if needed

      setSaveMessage('Settings saved successfully!');
      setSaveError(null);
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveError('Failed to save settings. Please try again.');
      setSaveMessage(null);
      setTimeout(() => setSaveError(null), 3000);
    }
  };

  const resetSettings = () => {
    setAppSettings({
      apiBaseUrl: 'https://localhost:5001/api',
      syncInterval: 30,
      maxRetries: 3,
      requestTimeout: 30000,
      enableLogging: true,
      logLevel: 'Info',
    });
    setNotificationSettings({
      enableOrderNotifications: true,
      enableSyncNotifications: true,
      enableErrorNotifications: true,
      soundEnabled: true,
      notificationDuration: 5000,
    });
    setSyncSettings({
      autoSyncEnabled: true,
      syncInterval: 30,
      syncOnStartup: true,
      syncMenuItems: true,
      syncOrders: true,
      syncBranches: true,
    });
  };

  const handleAddApiKey = () => {
    if (newApiKey.name && newApiKey.value) {
      const newKey = {
        id: Date.now(),
        name: newApiKey.name,
        value: newApiKey.value,
        created: new Date().toISOString().split('T')[0],
      };
      setApiKeys([...apiKeys, newKey]);
      setNewApiKey({ name: '', value: '' });
      setNewApiKeyDialog(false);
    }
  };

  const handleDeleteApiKey = (id: number) => {
    setApiKeys(apiKeys.filter(key => key.id !== id));
  };

  const testConnection = async () => {
    try {
      // Test API connection
      const response = await fetch(`${appSettings.apiBaseUrl}/health`);
      if (response.ok) {
        setSaveMessage('Connection test successful!');
        setSaveError(null);
      } else {
        setSaveError('Connection test failed. Please check your API URL.');
        setSaveMessage(null);
      }
    } catch (error) {
      setSaveError('Connection test failed. Please check your API URL.');
      setSaveMessage(null);
    }
    setTimeout(() => {
      setSaveMessage(null);
      setSaveError(null);
    }, 3000);
  };  return (
    <Box sx={{ p: 2, maxHeight: 'calc(100vh - 64px)', overflowY: 'auto' }}> {/* Settings available to all users */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <PermissionGate permissions={["system:superadmin_manage", "*:*"]}>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={resetSettings}
            >
              Reset to Defaults
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveSettings}
            >
              Save Changes
            </Button>
          </Box>
        </PermissionGate>
      </Box>

      {saveMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {saveMessage}
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<ApiIcon />} label="General" />
          <Tab icon={<NotificationsIcon />} label="Notifications" />
          <Tab icon={<SyncIcon />} label="Sync" />
          <Tab icon={<SecurityIcon />} label="API Keys" />
          <Tab icon={<StorageIcon />} label="Data" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>      {/* General Settings */}
        <Box display="flex" flexWrap="wrap" gap={3}>
          <Box flex={1} minWidth="300px">
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  API Configuration
                </Typography>
                <Box component="form" sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="API Base URL"
                    value={appSettings.apiBaseUrl}
                    onChange={(e) => setAppSettings({ ...appSettings, apiBaseUrl: e.target.value })}
                    margin="normal"
                    helperText="The base URL for the Talabat WebApi"
                  />
                  <TextField
                    fullWidth
                    label="Request Timeout (ms)"
                    type="number"
                    value={appSettings.requestTimeout}
                    onChange={(e) => setAppSettings({ ...appSettings, requestTimeout: parseInt(e.target.value) })}
                    margin="normal"
                    inputProps={{ min: 1000, max: 60000 }}
                  />
                  <TextField
                    fullWidth
                    label="Max Retries"
                    type="number"
                    value={appSettings.maxRetries}
                    onChange={(e) => setAppSettings({ ...appSettings, maxRetries: parseInt(e.target.value) })}
                    margin="normal"
                    inputProps={{ min: 0, max: 10 }}
                  />
                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      onClick={testConnection}
                      fullWidth
                    >
                      Test Connection
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box flex={1} minWidth="300px">
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Application Settings
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={theme === 'dark'}
                        onChange={(e) => dispatch(setTheme(e.target.checked ? 'dark' : 'light'))}
                      />
                    }
                    label="Dark Mode"
                  />                  <FormControlLabel
                    control={
                      <Switch
                        checked={appSettings.enableLogging}
                        onChange={(e) => setAppSettings({ ...appSettings, enableLogging: e.target.checked })}
                      />
                    }
                    label="Enable Logging"
                  />
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Log Level</InputLabel>
                    <Select
                      value={appSettings.logLevel}
                      label="Log Level"
                      onChange={(e) => setAppSettings({ ...appSettings, logLevel: e.target.value })}
                      disabled={!appSettings.enableLogging}
                    >
                      <MenuItem value="Debug">Debug</MenuItem>
                      <MenuItem value="Info">Info</MenuItem>
                      <MenuItem value="Warning">Warning</MenuItem>
                      <MenuItem value="Error">Error</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Notification Settings */}
        <Card>
          <CardContent>            <Typography variant="h6" gutterBottom>
              Notification Preferences
            </Typography>
            <Stack spacing={3}>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.enableOrderNotifications}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, enableOrderNotifications: e.target.checked })}
                    />
                  }
                  label="Order Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.enableSyncNotifications}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, enableSyncNotifications: e.target.checked })}
                    />
                  }
                  label="Sync Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.enableErrorNotifications}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, enableErrorNotifications: e.target.checked })}
                    />
                  }
                  label="Error Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.soundEnabled}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, soundEnabled: e.target.checked })}
                    />
                  }
                  label="Sound Notifications"                />
              </Box>
              <Box>
                <Typography gutterBottom>
                  Notification Duration: {notificationSettings.notificationDuration / 1000}s
                </Typography>
                <Slider
                  value={notificationSettings.notificationDuration}
                  onChange={(e, value) => setNotificationSettings({ ...notificationSettings, notificationDuration: value as number })}
                  min={2000}
                  max={10000}
                  step={1000}
                  marks={[
                    { value: 2000, label: '2s' },
                    { value: 5000, label: '5s' },
                    { value: 10000, label: '10s' },
                  ]}                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </TabPanel>      <TabPanel value={tabValue} index={2}>
        {/* Sync Settings */}
        <Stack spacing={3}>
          <Box>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Auto Sync Configuration
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncSettings.autoSyncEnabled}
                      onChange={(e) => setSyncSettings({ ...syncSettings, autoSyncEnabled: e.target.checked })}
                    />
                  }
                  label="Enable Auto Sync"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncSettings.syncOnStartup}
                      onChange={(e) => setSyncSettings({ ...syncSettings, syncOnStartup: e.target.checked })}
                    />
                  }
                  label="Sync on Startup"
                />
                <TextField
                  fullWidth
                  label="Sync Interval (minutes)"
                  type="number"
                  value={syncSettings.syncInterval}
                  onChange={(e) => setSyncSettings({ ...syncSettings, syncInterval: parseInt(e.target.value) })}
                  margin="normal"
                  inputProps={{ min: 1, max: 1440 }}                  disabled={!syncSettings.autoSyncEnabled}
                />
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sync Components
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncSettings.syncMenuItems}
                      onChange={(e) => setSyncSettings({ ...syncSettings, syncMenuItems: e.target.checked })}
                    />
                  }
                  label="Menu Items"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncSettings.syncOrders}
                      onChange={(e) => setSyncSettings({ ...syncSettings, syncOrders: e.target.checked })}
                    />
                  }
                  label="Orders"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncSettings.syncBranches}
                      onChange={(e) => setSyncSettings({ ...syncSettings, syncBranches: e.target.checked })}
                    />
                  }                  label="Branches"
                />
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {/* API Keys Management */}
        <Card>
          <CardContent>            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                API Keys Management
              </Typography>              <PermissionGate permissions={["system:superadmin_manage", "*:*"]}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setNewApiKeyDialog(true)}
                >
                  Add API Key
                </Button>
              </PermissionGate>
            </Box>
            <List>
              {apiKeys.map((apiKey) => (
                <React.Fragment key={apiKey.id}>
                  <ListItem>
                    <ListItemText
                      primary={apiKey.name}
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span">
                            Value: {apiKey.value}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Created: {apiKey.created}
                          </Typography>
                        </Box>
                      }
                    />                    <ListItemSecondaryAction>                      <PermissionGate permissions={["system:superadmin_manage", "*:*"]}>
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteApiKey(apiKey.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </PermissionGate>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>      <TabPanel value={tabValue} index={4}>
        {/* Data Management */}
        <Stack spacing={3}>
          <Box>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Data Export
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Export your data for backup or migration purposes.
                </Typography>                <Box display="flex" flexDirection="column" gap={1} mt={2}>
                  <PermissionGate permissions={["system:superadmin_manage", "*:*"]}>
                    <Button variant="outlined" fullWidth>
                      Export All Data
                    </Button>
                    <Button variant="outlined" fullWidth>
                      Export Orders Only
                    </Button>
                    <Button variant="outlined" fullWidth>
                      Export Mappings Only
                    </Button>
                  </PermissionGate>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Data Management
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Manage your application data and cache.
                </Typography>                <Box display="flex" flexDirection="column" gap={1} mt={2}>
                  <PermissionGate permissions={["system:superadmin_manage", "*:*"]}>
                    <Button variant="outlined" fullWidth>
                      Clear Cache
                    </Button>
                    <Button variant="outlined" color="warning" fullWidth>
                      Reset All Settings
                    </Button>
                    <Button variant="outlined" color="error" fullWidth>
                      Clear All Data
                    </Button>
                  </PermissionGate>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </TabPanel>

      {/* Add API Key Dialog */}
      <Dialog open={newApiKeyDialog} onClose={() => setNewApiKeyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Key Name"
            fullWidth
            variant="outlined"
            value={newApiKey.name}
            onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Key Value"
            fullWidth
            variant="outlined"
            type="password"
            value={newApiKey.value}
            onChange={(e) => setNewApiKey({ ...newApiKey, value: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewApiKeyDialog(false)}>Cancel</Button>
          <Button onClick={handleAddApiKey} variant="contained">
            Add Key
          </Button>        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
